import React, { useState } from 'react'
import { DialogTitle, DialogActions, Box, Typography } from '@mui/material'
import { ThemeButton } from '../theme-button'
import { StyledDialogContent } from './styled-dialog-content.component'

/**
 * @typedef {object} ConfirmationDialogProps
 * @property {string} [title='Warning:'] - El título del diálogo que se muestra en la parte superior.
 * @property {string} [text='Are you sure you want to proceed?'] - El texto del mensaje que se muestra en el cuerpo del diálogo.
 * @property {string} [yesText='Yes'] - El texto del botón de confirmación.
 * @property {'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'info' | 'success'} [yesColor='primary'] - El color del botón de confirmación.
 * @property {function(): void | Promise<void>} [yesAction] - La función callback que se ejecuta cuando se hace clic en el botón de confirmación. Puede ser asíncrona.
 * @property {boolean} [yesAndClose=true] - Si es `true`, el diálogo se cierra automáticamente después de ejecutar `yesAction` exitosamente.
 * @property {string} [noText='No'] - El texto del botón de cancelación.
 * @property {'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'info' | 'success'} [noColor='default'] - El color del botón de cancelación.
 * @property {function(): void} [noAction] - La función callback que se ejecuta cuando se hace clic en el botón de cancelación.
 * @property {boolean} [noAndClose=true] - Si es `true`, el diálogo se cierra automáticamente después de ejecutar `noAction`.
 * @property {function(): void} close - Función para cerrar el diálogo.
 */

/**
 * Un componente de diálogo de confirmación con dos botones: aceptar y cancelar.
 * 
 * Este diálogo se utiliza para solicitar confirmación del usuario antes de ejecutar acciones
 * importantes o potencialmente destructivas. Incluye manejo de estados de carga para acciones
 * asíncronas, mostrando un indicador de progreso en el botón de confirmación mientras se
 * ejecuta la acción.
 * 
 * El componente maneja automáticamente errores en acciones asíncronas, evitando que el diálogo
 * se cierre si la acción falla y desactivando el estado de carga para permitir al usuario
 * intentar nuevamente.
 * 
 * @component
 * @example
 * const MyComponent = () => {
 *   const { openDialog } = useDialog();
 * 
 *   const handleDelete = async () => {
 *     openDialog(ConfirmationDialog, {
 *       title: 'Confirmar Eliminación',
 *       text: '¿Está seguro de que desea eliminar este elemento? Esta acción no se puede deshacer.',
 *       yesText: 'Eliminar',
 *       yesColor: 'error',
 *       yesAction: async () => {
 *         await deleteItem(itemId);
 *         showSuccessMessage('Elemento eliminado correctamente');
 *       },
 *       noText: 'Cancelar',
 *       noColor: 'default',
 *     });
 *   };
 * 
 *   return <button onClick={handleDelete}>Eliminar</button>;
 * };
 * 
 * @param {ConfirmationDialogProps} props - Las props del componente ConfirmationDialog.
 */
export const ConfirmationDialog = ({
  title = 'Warning:',
  text = 'Are you sure you want to proceed?',
  yesText = 'Yes',
  yesColor = 'primary',
  yesAction = () => {},
  yesAndClose = true,
  noText = 'No',
  noColor = 'default',
  noAction = () => {},
  noAndClose = true,
  close,
}) => {
  const [loading, setLoading] = useState(false)
  const confirmAction = async () => {
    setLoading(true)
    try {
      await yesAction()
      setLoading(false)
      if (yesAndClose) {
        close()
      }
    } catch {
      setLoading(false)
    }
  }
  return (
    <Box p={4}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="h4" color="primary">
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <StyledDialogContent>
        <Typography variant="body1">{text}</Typography>
      </StyledDialogContent>
      <DialogActions>
        <ThemeButton
          labelM="sm"
          color={noColor}
          onClick={() => {
            noAction()
            if (noAndClose) {
              close()
            }
          }}
        >
          {noText}
        </ThemeButton>
        <ThemeButton labelM="sm" color={yesColor} loading={loading} onClick={confirmAction}>
          {yesText}
        </ThemeButton>
      </DialogActions>
    </Box>
  )
}
